import { ConcessionMessageType } from '../../models/Concession'
import { toYYYYMMDD } from '../../helpers/date'

export interface ConcessionFormValues {
    name: string
    description: string
    is_active: boolean
    start_date: string
    /** Raw input string; validated as an integer >= 1. */
    duration_days: string
}

/**
 * Mirror the API validation rules (CONCESSIONS.md section 5) for good UX.
 */
export const validateConcession = (values: ConcessionFormValues): string[] => {
    const errors: string[] = []

    // Rule 1: name is required.
    if (!values.name.trim()) {
        errors.push('Name is required.')
    }

    // Description is required.
    if (!values.description.trim()) {
        errors.push('Description is required.')
    }

    // Rule 2: start_date is required.
    if (!values.start_date) {
        errors.push('Start date is required.')
    }

    // Rule 3: duration_days must be an integer >= 1.
    const duration = Number(values.duration_days)
    if (!values.duration_days || !Number.isInteger(duration) || duration < 1) {
        errors.push('Duration must be a whole number of at least 1 day.')
    }

    return errors
}

export interface ConcessionMessageFormValues {
    message_type: ConcessionMessageType
    subject: string
    content: string
    content_html: string
    send_at: Date | null
    /** Concession lifetime, `YYYY-MM-DD`. */
    lifetime_start: string
    lifetime_end: string
}

/** Mirror the message API validation rules (CONCESSIONS.md section 6). */
export const validateConcessionMessage = (
    values: ConcessionMessageFormValues,
): string[] => {
    const errors: string[] = []

    // Rule 1: content is required.
    if (!values.content.trim()) {
        errors.push('Message content is required.')
    }

    // Rule 2: text messages cannot have a subject or HTML body.
    if (values.message_type === 'text') {
        if (values.subject.trim()) {
            errors.push('Text messages cannot have a subject.')
        }
        if (values.content_html.trim()) {
            errors.push('Text messages cannot have a rich HTML body.')
        }
    }

    // Rule 3: send_at required and within the concession lifetime.
    if (!values.send_at) {
        errors.push('A send date and time is required.')
    } else {
        const sendDate = toYYYYMMDD(values.send_at)
        if (values.lifetime_start && sendDate < values.lifetime_start) {
            errors.push('Send time is before the concession lifetime starts.')
        }
        if (values.lifetime_end && sendDate > values.lifetime_end) {
            errors.push('Send time is after the concession lifetime ends.')
        }
    }

    return errors
}
